import {
  GetContrastColorBW,
  useFeedback,
  useTranslation,
} from "@opendash/core";
import { _User } from "@opendash/plugin-parse/dist/types-generated";
import { ColorPicker } from "@opendash/ui";
import { Button, Form, Input, InputNumber, Modal, Select } from "antd";
import Parse from "parse";
import React, { useContext, useEffect, useRef, useState } from "react";
import { AgencyContext } from "../contexts/AgencyContext";
import { useRouteType } from "../hooks/useRouteTypes";
import { Route } from "../js/parsegenerated";
import Infolabel from "./UI/Infolabel";

export interface RouteDataType {
  route_id: string;
  route_short_name: string;
  route_long_name: string;
  route_desc: string;
  route_type: number;
  route_url: string;
  route_color: string;
  route_text_color: string;
  route_sort_order: number;
}

export const RouteUpdateModal = (props: {
  open: boolean;
  route: Route;
  onOk: Function;
  onCancel: Function;
}) => {
  const t = useTranslation();
  const { message } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [color, setColor] = useState<string>("#FFFFFF");
  const [textColor, setTextColor] = useState<string>("#000000");
  const [routeTypes] = useRouteType();
  const { TextArea } = Input;
  const { Option } = Select;
  const state = useContext(AgencyContext);

  useEffect(() => {
    if (formRef.current) form.resetFields();
  }, [props.route]);

  const save = async () => {
    form.validateFields().then(async (values: RouteDataType) => {
      setLoading(true);

      const savedroute = await props.route.save({
        agency_id: state.selectedAgency,
        route_id: values.route_id,
        route_short_name: values.route_short_name,
        route_long_name: values.route_long_name,
        route_desc: values.route_desc,
        route_type: values.route_type,
        route_url: values.route_url,
        route_color: values.route_color,
        route_text_color: values.route_text_color,
        route_sort_order: values.route_sort_order,
        tenant: (Parse.User.current() as _User).get("tenant"),
      });

      if (!values.route_id) {
        savedroute.set("route_id", savedroute.id);
        await savedroute.save();
      }
      message.success(t("gtfs:route.update.modal.success"));
      props.onOk(savedroute);
      form.resetFields();
      setLoading(false);
    });
  };

  return (
    <>
      <Modal
        title={t("gtfs:route.create.modal.title")}
        open={props.open}
        onOk={save}
        onCancel={() => {
          props.onCancel();
          form.resetFields();
        }}
        width={"80%"}
        getContainer={false}
        footer={[
          <Button
            key="back"
            onClick={() => {
              props.onCancel();
              form.resetFields();
            }}
          >
            {t("gtfs:global.button.cancel_label")}
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={save}>
            {t("gtfs:global.button.save_label")}
          </Button>,
        ]}
      >
        <Form autoComplete="off" form={form} layout="vertical" ref={formRef}>
          <Form.Item
            name="route_id"
            initialValue={props.route ? props.route.get("route_id") : null}
            label={t("gtfs:route.create.modal.form.id.label")}
            help={t("gtfs:route.create.modal.form.id.help")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_short_name"
            initialValue={
              props.route ? props.route.get("route_short_name") : null
            }
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.short_name.label")}
                info={t("gtfs:route.create.modal.form.short_name.info")}
              />
            }
            help={t("gtfs:route.create.modal.form.short_name.help")}
            rules={[
              {
                required: true,
                message: t(
                  "gtfs:route.create.modal.form.short_name.rules.required"
                ),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_long_name"
            initialValue={
              props.route ? props.route.get("route_long_name") : null
            }
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.long_name.label")}
                info={t("gtfs:route.create.modal.form.long_name.info")}
              />
            }
            help={t("gtfs:route.create.modal.form.long_name.help")}
            rules={[
              {
                required: true,
                message: t(
                  "gtfs:route.create.modal.form.long_name.rules.required"
                ),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_desc"
            initialValue={props.route ? props.route.get("route_desc") : null}
            label={t("gtfs:route.create.modal.form.desc.label")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="route_type"
            initialValue={props.route ? props.route.get("route_type") : null}
            label={t("gtfs:route.create.modal.form.type.label")}
            rules={[
              {
                required: true,
                message: t("gtfs:route.create.modal.form.type.rules.required"),
              },
            ]}
          >
            <Select>
              {routeTypes.map((routetype) => (
                <Option key={routetype.id} value={routetype.value}>
                  {routetype.get("transportation_device") +
                    " - " +
                    routetype.get("description")}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="route_url"
            initialValue={props.route ? props.route.get("route_url") : null}
            label={t("gtfs:route.create.modal.form.url.label")}
            help={t("gtfs:route.create.modal.form.url.help")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_color"
            initialValue={props.route ? props.route.get("route_color") : null}
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.color.label")}
                info={t("gtfs:route.create.modal.form.color.info")}
              />
            }
            rules={[
              {
                required: false,
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <ColorPicker
              value={color}
              onChange={(value) => {
                setColor(value);
              }}
            />
          </Form.Item>
          <Button
            onClick={() => setColor(GetContrastColorBW(textColor))}
            style={{ marginBottom: 24 }}
          >
            {t("gtfs:route.create.modal.form.text_color.auto_button")}
          </Button>
          <Form.Item
            name="route_text_color"
            initialValue={
              props.route ? props.route.get("route_text_color") : null
            }
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.text_color.label")}
                info={t("gtfs:route.create.modal.form.text_color.info")}
              />
            }
            rules={[
              {
                required: false,
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <ColorPicker
              value={textColor}
              onChange={(value) => {
                setTextColor(value);
              }}
            />
          </Form.Item>
          <Button
            onClick={() => setTextColor(GetContrastColorBW(color))}
            style={{ marginBottom: 24 }}
          >
            {t("gtfs:route.create.modal.form.text_color.auto_button")}
          </Button>
          <Form.Item
            name="route_sort_order"
            initialValue={
              props.route ? props.route.get("route_sort_order") : null
            }
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.sort_order.label")}
                info={t("gtfs:route.create.modal.form.sort_order.info")}
              />
            }
            help={t("gtfs:route.create.modal.form.sort_order.help")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
